import { Box, Button, InputText, Modal, Text, useToast } from '@driftt/dds'
import { EmailWysiwyg } from '@driftt/email-wysiwyg'
import { integrationStatus } from '@driftt/traffic-control'
import { equals, merge, pathOr } from 'ramda'
import React, { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'

import { upload } from 'api/file'
import { createNewContentVariable, updateContentVariable } from 'api/playbooks'
import { isEmptyOrNil } from 'utils/index'

import { DEMANDBASE_PROPERTIES } from './demandbase_properties'
import { DRIFT_INTEL_PROPERTIES } from './drift_intel_properties'

import './style.css'

const DEFAULT_VARIABLE = {
  name: null,
  content: null,
}
const convertPtoSpan = (variableContent) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(variableContent, 'text/html')

  const paragraphs = doc.querySelectorAll('p')

  if (paragraphs.length > 0) {
    const pElement = paragraphs[0]
    const spanElement = doc.createElement('span')
    spanElement.innerHTML = pElement.innerHTML
    pElement.parentNode.replaceChild(spanElement, pElement)
  }
  return doc.body.innerHTML
}

const convertSpanToP = (variableContent) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(variableContent, 'text/html')

  const spans = doc.querySelectorAll('span')
  if (spans.length > 0) {
    const spanElement = spans[0]
    const pElement = doc.createElement('p')
    pElement.innerHTML = spanElement.innerHTML
    spanElement.parentNode.replaceChild(pElement, spanElement)
  }

  const updatedContent = doc.body.innerHTML

  return updatedContent
}

const EMPTY_EDITOR_HTML = '<p></p>'

const EditVariableModal = ({
  isNew = false,
  variable = null,
  isOpen = false,
  onClose,
}) => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [variableName, setVariableName] = useState(isNew ? '' : variable.name)
  const [variableContent, setVariableContent] = useState(
    isNew ? EMPTY_EDITOR_HTML : convertSpanToP(variable.content)
  )
  const [localVariable, setLocalVariable] = useState(
    isNew ? DEFAULT_VARIABLE : variable
  )

  const userTagList = useSelector(
    pathOr([], ['__BOT_EDITOR_v2', 'endUserAttributes', 'editorProperties'])
  )

  const saveNewVariable = useMutation(
    ({ name, content }) =>
      createNewContentVariable(name, convertPtoSpan(content)),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(`playbook-content-variables--all`)
        toast({
          message: `Successfully created ${data.data.name}`,
          variant: 'success',
        })
        onClose()
      },
      onError: () => {
        toast({
          message: `Could not create variable. Contact support if this continues.`,
          variant: 'error',
        })
      },
    }
  )

  const modifyVariableContent = (variable) => {
    const updatedContent = convertPtoSpan(variableContent)
    const newVar = {
      ...variable,
      content: updatedContent,
    }
    return newVar
  }
  const saveExistingVariable = useMutation(
    (variable) => updateContentVariable(modifyVariableContent(variable)),
    {
      onSuccess: (data) => {
        toast({
          message: `Successfully updated ${data.data.name}`,
          variant: 'success',
        })
        onClose()
      },
      onError: () => {
        toast({
          message: `Could not update variable. Contact support if this continues.`,
          variant: 'error',
        })
      },
    }
  )

  useEffect(() => {
    if (
      !equals(localVariable.name, variableName) ||
      !equals(localVariable.content, variableContent)
    ) {
      const newVar = merge(variable, {
        name: variableName,
        content: variableContent,
      })
      setLocalVariable(newVar)
    }
  }, [
    localVariable.name,
    localVariable.content,
    variableName,
    variableContent,
    variable,
  ])

  const isDemandbaseConnected =
    integrationStatus.isIntegrationConnected('demandbase')

  return (
    <Modal.Root onClose={() => onClose()} open={isOpen}>
      <Modal.Dialog title="Create variable" size="x-large" variant="info">
        <Modal.Title title={`${isNew ? 'Create' : 'Edit'} variable`} />
        <Modal.Content css={{ width: '600px', height: '350px' }}>
          <Box css={{ marginBottom: '$3' }} fullWidth>
            <InputText
              label="Variable name"
              value={variableName}
              onChange={({ target }) => setVariableName(target.value)}
              size="full-width"
            />
          </Box>
          <Box width="100%">
            <Text css={{ marginBottom: '$1' }}>Variable content</Text>
            <EmailWysiwyg
              placeholder={'Set your variable content here'}
              className="edit-variables--input-content"
              html={variableContent}
              onChange={(val) => {
                setVariableContent(val)
              }}
              userTagList={userTagList}
              driftIntelTagList={
                !isDemandbaseConnected ? DRIFT_INTEL_PROPERTIES : null
              }
              demandbaseTagList={
                isDemandbaseConnected ? DEMANDBASE_PROPERTIES : null
              }
              toolbarOptions={[
                'bold',
                'italic',
                'underline',
                'numbered-list',
                'bulleted-list',
                'link',
                'emoji',
                'image',
                'personalization',
              ]}
              hideImageControls={true}
              handleImageUpload={upload}
            />
          </Box>
        </Modal.Content>
        <Modal.Actions>
          <Button variant="secondary" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              isNew
                ? saveNewVariable.mutate(localVariable)
                : saveExistingVariable.mutate(localVariable)
            }
            disabled={
              equals(variableContent, EMPTY_EDITOR_HTML) ||
              isEmptyOrNil(variableName)
            }
          >
            Save
          </Button>
        </Modal.Actions>
      </Modal.Dialog>
    </Modal.Root>
  )
}

export default EditVariableModal
